/*
 * Pad design selector (BH + Pad bundle PDPs, snippets/pad-design-selector.liquid).
 *
 * The radios in that snippet are the only submitted control — there is no hidden pad input. The server
 * checks the first available design and renders the unavailable ones natively `disabled`, which is the
 * no-JS gate. enhanceRadios() strips that attribute so all three become clickable, and must re-run after
 * every #PadDesignOptions refresh because product-info.js re-imports the server HTML while
 * connectedCallback does not run again. Availability therefore comes from class="disabled" only: this
 * element never sets radio.disabled, since disabling the focused radio drops focus to <body> and kills
 * arrow-key navigation.
 *
 * The client-side gate is the submit button's native `disabled`, and only that. aria-disabled belongs to
 * product-form.js as its in-flight /cart/add and error lock; clearing it from here allowed a second
 * concurrent add.
 *
 * `intent` is the design the customer is expressing: seeded from the server-checked radio, then changed
 * only by a click and never recalculated, so a choice survives M -> 2XL -> M.
 *
 * product-info.js owns the options refresh, so a stale design is impossible even if this element never
 * upgrades. Read data-variant-purchasable from event.data.html — product-info.js only calls
 * toggleSubmitButton() and never copies that attribute, so the live one is stale after a change.
 *
 * Globals (classic scripts): subscribe / PUB_SUB_EVENTS (pubsub.js, constants.js).
 */
if (!customElements.get('pad-design-selector')) {
  customElements.define(
    'pad-design-selector',
    class PadDesignSelector extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      optionChangeUnsubscriber = undefined;
      variantUpdatePending = false;
      intent = null;

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        // Quick-add clone: card-product.liquid already suppresses it for flagged products, so this is
        // the belt. Emptying the element is not enough — the cloned button is server-enabled, so block
        // it first. Setting aria-disabled here is safe only because this instance never unblocks.
        if (this.sectionId?.startsWith('quickadd-') || this.closest('product-info')?.dataset.originalSection) {
          this.closest('product-info')
            ?.querySelectorAll('button.product-form__submit')
            .forEach((button) => {
              button.setAttribute('disabled', 'disabled');
              button.setAttribute('aria-disabled', 'true');
            });
          this.classList.add('hidden');
          this.innerHTML = '';
          return;
        }

        this.addEventListener('change', this.onChange.bind(this));
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        this.optionChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.onOptionValueSelectionChange.bind(this)
        );

        this.readVariantPurchasable(this.closest('product-info'));
        this.enhanceRadios();
        this.intent = this.checkedDesign;
        this.reconcile();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.optionChangeUnsubscriber?.();
      }

      get productFormElement() {
        return this.closest('product-info')?.querySelector('product-form');
      }

      get submitButton() {
        return this.closest('product-info')?.querySelector(`#ProductSubmitButton-${this.sectionId}`);
      }

      get optionsWrapper() {
        return this.querySelector('[data-pad-options]');
      }

      get radios() {
        return Array.from(this.querySelectorAll('input[data-pad-design]'));
      }

      get checkedDesign() {
        return this.querySelector('input[data-pad-design]:checked')?.value || null;
      }

      // Never consult radio.disabled here — that is the no-JS gate, and enhanceRadios() clears it.
      available(radio) {
        return !radio.classList.contains('disabled');
      }

      // Server rendering -> enhanced rendering. Re-run after every refresh: the fresh HTML is disabled again.
      enhanceRadios() {
        this.radios.forEach((radio) => {
          radio.disabled = false;
        });
      }

      // Server verdict on the variant itself (available / quantity rule / hidden), from buy-buttons.liquid.
      // Needed on the same-variant path so an available design never unblocks an unpurchasable variant.
      readVariantPurchasable(root) {
        const button = root?.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        this.variantPurchasable = button?.getAttribute('data-variant-purchasable') === 'true';
      }

      // `disabled` is also in sticky-atc.js's observed attributeFilter, so the mobile bar re-syncs with us.
      blockSubmit() {
        this.submitButton?.setAttribute('disabled', 'disabled');
      }

      // Releases only the pad-owned `disabled`. A busy or errored form keeps its own aria-disabled lock.
      unblockSubmit() {
        const button = this.submitButton;
        if (!button?.hasAttribute('disabled')) return;
        this.productFormElement?.toggleSubmitButton?.(false);
      }

      onChange(event) {
        const radio = event.target;
        if (!radio.matches('input[data-pad-design]') || !radio.checked) return;
        this.intent = radio.value;
        // Mid-fetch click: record the intent, let the matching fresh render reconcile it.
        if (this.variantUpdatePending) return;
        this.reconcile();
      }

      // Until the matching variantChange lands the radios still describe the OLD variant while the form's
      // name="id" may already point at the new one, so fail-close the whole pending window.
      onOptionValueSelectionChange(event) {
        if (!this.closest('product-info')?.contains(event.data.target)) return;
        this.variantUpdatePending = true;
        this.blockSubmit();
      }

      onVariantChange(event) {
        if (event.data.sourceSectionId !== this.sectionId) return;

        this.variantUpdatePending = false;

        // Defensive: the fetched section renders no pad options (requires_pads off, or block removed).
        if (!event.data.html.getElementById(`PadDesignOptions-${this.sectionId}`) || !this.optionsWrapper) {
          this.markUnavailable();
          return;
        }

        // product-info.js has already swapped the options in, complete with the server `disabled`.
        this.enhanceRadios();

        const fetchedButton = event.data.html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.variantPurchasable = fetchedButton?.getAttribute('data-variant-purchasable') === 'true';

        // Seed an absent intent only — a real choice is never overwritten. Without this, a first render
        // with zero available designs would leave nothing selected while the server submits a default.
        if (!this.intent) this.intent = this.checkedDesign;

        this.reconcile();
      }

      // Maps `intent` + server availability onto the radios, the submit button and the live region.
      // The radios ARE the submitted state, so checking one is the whole write.
      reconcile() {
        this.classList.remove('pad-design-selector--collapsed');
        this.optionsWrapper?.classList.remove('hidden');

        const radios = this.radios;
        const target = this.intent ? radios.find((radio) => radio.value === this.intent) : null;
        // Force the retained choice over the freshly rendered server default. Setting .checked
        // programmatically does not re-fire change.
        radios.forEach((radio) => {
          radio.checked = radio === target;
        });

        if (target && this.available(target)) {
          this.setMessage('');
          // An unpurchasable variant keeps the server's own disabled state and label.
          if (this.variantPurchasable) this.unblockSubmit();
          return;
        }

        this.blockSubmit();
        // An expressed choice always names itself; "no design at all" is carried by the button label.
        this.setMessage(target ? target.dataset.padReason : this.dataset.padNoneMessage);
      }

      // Never replaces the node and never removes it from rendering — a live region has to exist and be
      // rendered before its content changes. Writes are synchronous (a frame callback does not run while
      // the page is hidden), and identical text is left alone: blanking it to force a mutation collapsed
      // the box and shifted the button below it.
      setMessage(text) {
        const element = this.querySelector('[data-pad-message]');
        if (!element) return;
        if (element.textContent === text) return;
        element.textContent = text;
      }

      // From product-info.js setUnavailable() (an option combination with no variant), and from the
      // defensive branch above. `intent` survives, so returning to a valid variant restores the choice.
      // The root stays rendered because it carries the live region; --collapsed drops its leftover margin.
      markUnavailable() {
        this.blockSubmit();
        this.setMessage('');
        this.optionsWrapper?.classList.add('hidden');
        this.classList.add('pad-design-selector--collapsed');
      }
    }
  );
}
